import { s } from '@/theme/tokens';
import { Box } from '@/ui/primitives';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';

export const CHART_EMPTY_STATE_FILTER_HINT = 'Try adjusting filters or search.';
export const CHART_EMPTY_STATE_NO_DATA_LABEL = 'No data';
export const CHART_EMPTY_STATE_NO_DATA_SX = {
  color: 'text.secondary',
  fontSize: '15px',
  fontWeight: 400,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.15,
} as const;

type ChartEmptyStateVariant = 'default' | 'metricCard';

type ChartEmptyStateProps = {
  message?: string;
  title?: string;
  body?: string;
  contentSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  variant?: ChartEmptyStateVariant;
};

export const ChartEmptyState = ({
  message = 'NO DATA',
  title,
  body,
  contentSx,
  titleSx,
  variant = 'default',
}: ChartEmptyStateProps): ReactElement => {
  const noDataTitleSx =
    title === CHART_EMPTY_STATE_NO_DATA_LABEL || variant === 'metricCard'
      ? CHART_EMPTY_STATE_NO_DATA_SX
      : null;
  const resolvedContentSx: SxProps<Theme> = [
    {
      display: 'flex',
      flexDirection: 'column',
      gap: s('xxs'),
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
    },
    ...(Array.isArray(contentSx) ? contentSx : contentSx == null ? [] : [contentSx]),
  ];
  const resolvedTitleSx: SxProps<Theme> = [
    { fontWeight: 400 },
    ...(noDataTitleSx ? [noDataTitleSx] : []),
    ...(Array.isArray(titleSx) ? titleSx : titleSx == null ? [] : [titleSx]),
  ];
  if (title || body) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          textAlign: 'center',
        }}
      >
        <Box sx={resolvedContentSx}>
          {title ? <Box sx={resolvedTitleSx}>{title}</Box> : null}
          {body ? <Box>{body}</Box> : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        fontSize: '14px',
      }}
    >
      <Box sx={{ fontWeight: 400, textTransform: 'uppercase' }}>{message}</Box>
    </Box>
  );
};
